import { CardContent, CardHeader, Stack } from "@mui/material";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import MaterialTypography from "../../../components/MaterialTypography";
import { useNavigate } from "react-router-dom";
import Table from "../../../components/Table";
import { useEffect, useState } from "react";
import { deleteData, fetchData, postData } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import { useSnackbar } from "../../../context/popup";
import UploadFile from "../../Utils/UploadFile";
import ExternalApiRegistryFilterDialog from "./components/APIendpointsFilterDialog";
import { useLoading } from "../../../components/Loading";
import { FilterAlt } from "@mui/icons-material";

const APIendpointsList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector(
    (state) => state?.ExternalApiRegistryStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.ExternalApiRegistryStore?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.ExternalApiRegistryStore?.totalPages
  );

  const [isRefresh, setIsRefresh] = useState(false);
  const [apiRegistry, setApiRegistry] = useState([]);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
    apiRegistryId: "",
    protocolId: "",
    environmentId: "",
    authTypeId: ""
  });

  useEffect(() => {
    fetchAPIendpointsList();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //List API
  const fetchAPIendpointsList = async () => {
    setCircleLoading(true);
    try {
      let url = `/api-engine/api-endpoint?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;

      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      if (filterData.apiRegistryId !== "") {
        url += `&apiRegistryId=${filterData.apiRegistryId}`;
      }

      if (filterData.protocolId !== "") {
        url += `&protocolId=${filterData.protocolId}`;
      }

      if (filterData.environmentId !== "") {
        url += `&environmentId=${filterData.environmentId}`;
      }

      if (filterData.authTypeId !== "") {
        url += `&authTypeId=${filterData.authTypeId}`;
      }

      const response = await fetchData(url);

      if (response?.success) {
        setApiRegistry(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      } else {
        //throw error
        showSnackbar("Error Fetching External Api End Points List", "error");
      }
    } catch (error) {
      console.error("Error Fetching External Api End Points:", error);
      showSnackbar(error?.response?.data?.message || "Error Fetching External Api End Points List", "error");
    } finally {
      setCircleLoading(false);
    }
  };

  //bulk file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true);
    setUploadSuccess(false);

    try {
      const formData = new FormData();

      formData.append("file", file);

      const result = await postData(`/api-engine/schema`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (result?.success) {
        setUploadSuccess(true);
        showSnackbar(result?.message, "success");
        setOpenUploadFileDialog(false);
        setClearFileOnSuccess(true);
        fetchAPIendpointsList();
      } else {
        setUploadSuccess(false);
        showSnackbar("Faiiled to upload file", "error");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      console.error("Upload error:", error || errorMessage);
      showSnackbar(errorMessage || "Network Error", "error");
    } finally {
      setFileUploading(false);
    }
  };

  //delete api
  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/api-engine/api-endpoint-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      if (response?.success) {
        showSnackbar("External Api End Points deleted successfully", "success");
      } else {
        showSnackbar("Failed to delete External Api End Points", "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar("Error Deleting External Api End Points", "error");
    }
  };

  //Table Mapping
  const APIendpointsListTable = {
    columns: [
      {
        key: "sno",
        label: "S.No.",
        renderCell: (row) => row.sno,
      },
      {
        key: "apiRegistry",
        label: "API Registry",
        renderCell: (row) => row.apiRegistry,
      },
      {
        key: "environment",
        label: "Environment",
        renderCell: (row) => row.environment,
      },
      {
        key: "endpointUrl",
        label: "Endpoint URL",
        renderCell: (row) => row.endpointUrl,
      },
      {
        key: "rateLimit",
        label: "Rate Limit",
        renderCell: (row) => row.rateLimit,
      },
      {
        key: "retryPolicy",
        label: "Retry Policy",
        renderCell: (row) => row.retryPolicy,
      },
      {
        key: "isActive",
        label: "Status",
        renderCell: (row) => (
          <span
            style={{
              color: row.isActive ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {row.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],

    rows: apiRegistry?.map((item, index) => ({
      id: item.id,
      sno: index + 1,

      /* ================= CORE ================= */
      apiRegistry: item.apiRegistry?.name ?? item.apiRegistry?.id ?? "-",

      environment:
        item.environment?.keyName ?? item.environment?.id ?? "-",

      endpointUrl: item.endpointUrl ?? "-",

      protocol: item.protocolDetails?.keyName ?? item.protocolId ?? "-",

      authType: item.authTypeDetails?.keyName ?? item.authTypeId ?? "-",

      /* ================= CONFIG SUMMARY ================= */
      rateLimit: item.rateLimitConfig
        ? `${item.rateLimitConfig.maxRequests}/${item.rateLimitConfig.perMilliseconds}ms`
        : "-",

      retryPolicy: item?.retryPolicy?.backoffStrategy
        ? `${item.retryPolicy.maxRetries} (${item.retryPolicy.backoffStrategy})`
        : "-",

      isActive: item.isActive,
    })),
  };

  const handleAddNewApiRegistry = () => {
    navigate(
      `/api-integration-engine/external-api-endpoints/external-api-endpoints-details`
    );
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,

    onEdit: (row) => {
      navigate(
        `/api-integration-engine/external-api-endpoints/external-api-endpoints-details?pageType=edit&id=${row.id}`
      );
    },
    onView: (row) => {
      navigate(
        `/api-integration-engine/external-api-endpoints/external-api-endpoints-details?pageType=view&id=${row.id}`
      );
    },
    onDelete: (row) => handleDelete(row),
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
               Api End Points List
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of Api End Points according to Insurer Registered.
              </MaterialTypography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>

              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewApiRegistry()}
              >
                Api End Points
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <CardContent>
        <Table
          columns={APIendpointsListTable?.columns}
          data={APIendpointsListTable?.rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel="No Api End Points found"
          handleSearch={handleSearch}
        />
      </CardContent>
      <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={["csv", "xlsx"]}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
        clearFileOnSuccess={clearFileOnSuccess}
      />
      <ExternalApiRegistryFilterDialog
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchAPIendpointsList(); // call API after filter applied
        }}
      />
    </DashboardLayout>
  );
};

export default APIendpointsList;
